import { masterClient } from '~/core/utils/clients/masterClient'
import type { CountryCode } from '~/core/i18n/types'
import type { MobileIdConfirmationStatus } from '~/features/Authorization/enums'

const RESOURCE = '/profile/confirmation/web'

type SendCodePayload = {
  phone: string,
  code: string,
  serviceId: string | null,
}

type CallCodeUuidPayload = {
  phone: string
}

type RequestFinishAuthPayload = {
  phone: string
  uuid: string
}

export type OAuthAuthorizationLinkPayload = {
  clientId: string,
  codeChallenge: string,
  state: string
  redirectUri: string
  phone: string
}

export type OAuthAuthorizationLinkDTO = {
  authorizationUrl: string
}

export type CompleteOAuthPayload = {
  state: string,
  code: string,
  codeVerifier: string
  deviceId?: string // Нужно для авторизации через VK ID
}

export type Country = {
  name: string
  iso: CountryCode
  phoneCode: string
  maskForInput: string
  flagPictureUrl: string
  pdDomain: string
  confirmPhoneBySmsEnabled: boolean
  confirmPhoneByOnlyCallEnabled: boolean
  confirmPhoneWebByCallEnabled: boolean
  confirmPhoneByOutgoingCallEnabled: boolean
  confirmPhoneByMtEnabled: boolean
  confirmPhoneByTgEnabled: boolean
  confirmPhoneByVkEnabled: boolean
  confirmPhoneByWhatsappEnabled: boolean
  confirmPhoneByMaxEnabled: boolean
  confirmPhoneByWbIdEnabled: boolean
  confirmPhoneByMobileIdEnabled: boolean,
  confirmPhoneByVkIdEnabled: boolean,
  confirmPhoneByYandexIdEnabled: boolean,
  personalDataAgreementLink: string,
  personalDataReceivingLink: string,
  thirdPartyPersonalDataAgreementLink: string,
  outgoingCallPhone: string,
  callCodePhone: string,
  telegramLink: string,
  whatsappLink: string,
  maxLink: string,
  supportPhone: string,
  supportEmail: string,
  supportMaxLink: string,
  supportTelegramLink: string,
  currency: string
}

export type CallCodeUuidDto = {
  phone: string
  uuid: string
}

export type AuthMethodDetailsDTO = {
  secretKey: string
  expiresIn: number
  hheUri: string | null
}

export const OUTGOING_CALL_SOCKET_MESSAGE_CONFIRMED = 'confirmed'

export default {
  requestCode(payload: {
    phone: string
    forceSms?: boolean
    gRecaptchaResponse?: string // TODO[sff_use_yandex_captcha]: удалить
    captchaResponse?: string
    confirmationFlow?: string
  }) {
    return masterClient.post<{
      code: 'call' | 'sms' | 'vk' | 'telegram' | 'whatsapp'
      message: string
      serviceId: string
      attemptsExceeded: boolean
      methodDetails?: AuthMethodDetailsDTO, // приходит только для mobile id
    }>(
      `${RESOURCE}/request/`,
      payload,
      {
        // Здесь не нужен retry
        'axios-retry': { retries: 0 },
        camelize: true,
      },
    )
  },
  confirmCode(payload: SendCodePayload) {
    return masterClient.post(
      `${RESOURCE}/confirm/`,
      { ...payload, mobileApp: false },
      {
        // Здесь не нужен retry
        'axios-retry': { retries: 0 },
        camelize: true,
      },
    )
  },
  requestCallCodeUuid(payload: CallCodeUuidPayload) {
    return masterClient.post<CallCodeUuidDto>(
      '/profile/confirmation/calls/create/',
      { ...payload, mobileApp: false },
      {
        // Здесь не нужен retry
        'axios-retry': { retries: 0 },
        camelize: true,
      },
    )
  },
  requestFinishAuth(payload: RequestFinishAuthPayload) {
    return masterClient.post(
      '/profile/confirmation/calls/finish/',
      { ...payload, mobileApp: false },
      {
        // Здесь не нужен retry
        'axios-retry': { retries: 0 },
        camelize: true,
      },
    )
  },
  requestFinishMobileIdAuth(serviceId: string, secretKey: string) {
    return masterClient.post<{ status: MobileIdConfirmationStatus}>(
      `${RESOURCE}/${serviceId}/finish/`,
      { secretKey },
      {
        'axios-retry': { retries: 0 },
        camelize: true,
      },
    )
  },
  getCountries() {
    return masterClient.get<Country[]>('/countries/', { camelize: true })
  },
  getOAuthAuthorizationLink(payload: OAuthAuthorizationLinkPayload) {
    return masterClient.get<OAuthAuthorizationLinkDTO>('/authorization/oauth2/external/authorize/', {
      camelize: true,
      params: { ...payload },
    })
  },
  completeOAuthLogin(payload: CompleteOAuthPayload) {
    return masterClient.post('/authorization/oauth2/external/login/', {
      ...payload,
    }, {
      camelize: true,
    })
  },
}
